import atsLogo from "assets/svg/ats-logo.svg";
import sapiensLogo from "assets/svg/sapiens-logo.png";
import { privateRoutes } from "constant/route/privateRoutes";
import {
  isComingSoonProducts,
  productUrlHelper,
} from "helpers/ProductUrlHelper";
import { isEffectivePlan } from "helpers/utils";
import { useAuth } from "hooks‬/Auth";
import { useNavigate } from "react-router-dom";

const Index = ({
  productTitle,
  productDescription,
  productGuid,
  isBought = true,
  isExpired = false,
  canSubscribe = true,
  handleAddBeta,
}: {
  productTitle: string;
  productDescription: string;
  isBought?: boolean;
  isExpired?: boolean;
  productGuid: string;
  canSubscribe?: boolean;
  handleAddBeta?: any;
}) => {
  const { subscriptionPlanConfiguration } = useAuth();
  const navigate = useNavigate();

  const redirectToProduct = (title: string) => {
    window.open(productUrlHelper(title), "WindowName", "noopener");
  };

  const isEffective = isEffectivePlan(
    subscriptionPlanConfiguration?.find(
      (item: any) =>
        item.subscriptionPlanProduct?.productType?.displayName === "Sapiens"
    )
  );

  const handleProductClick = () => {
    if (isBought && !isExpired) {
      redirectToProduct(productTitle.toUpperCase());
    } else if (
      (isBought && isExpired && canSubscribe) ||
      (!isBought && canSubscribe)
    ) {
      navigate(`${privateRoutes.Pricing}?Product=${productGuid}`);
    }
    // Non-admin, non-owner: do nothing — card is inert when there's nothing they can do
  };

  const productKey = productTitle?.toUpperCase();
  const productLogo = productKey === "HIRE" ? atsLogo : sapiensLogo;
  const comingSoon = isComingSoonProducts(productTitle);

  // A non-admin should not see purchase/renewal CTAs they can't act on
  const isActionable = isBought ? (isExpired ? canSubscribe : true) : canSubscribe;

  /* Derive status pill content */
  const statusClass = comingSoon
    ? "ni-dash-app-status--soon"
    : isBought
      ? isExpired
        ? "ni-dash-app-status--expired"
        : "ni-dash-app-status--active"
      : "ni-dash-app-status--locked";
  const statusLabel = comingSoon
    ? "Coming soon"
    : isBought
      ? isExpired
        ? "Expired"
        : "Active"
      : "Available";

  /* CTA button content */
  let ctaLabel: string;
  if (comingSoon) {
    ctaLabel = "Coming soon 🚀";
  } else if (isBought && isExpired) {
    ctaLabel = canSubscribe ? "Renew Subscription" : "Contact your admin";
  } else if (isBought) {
    ctaLabel = "Access " + productTitle.toLowerCase();
  } else if (canSubscribe) {
    ctaLabel = "Subscribe";
  } else {
    ctaLabel = "Contact your admin";
  }

  const ctaDisabled =
    comingSoon ||
    (!isBought && !canSubscribe) ||
    (isBought && isExpired && !canSubscribe);
  const ctaIsRenewal = isBought && isExpired && !ctaDisabled;

  return (
    <div
      className="ni-dash-app-card"
      data-product={productKey}
      data-soon={comingSoon ? "true" : "false"}
      onClick={isActionable && !comingSoon ? handleProductClick : undefined}
      role={isActionable && !comingSoon ? "button" : undefined}
      tabIndex={isActionable && !comingSoon ? 0 : undefined}
      onKeyDown={(e) => {
        if (isActionable && !comingSoon && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleProductClick();
        }
      }}
    >
      {/* Cover */}
      <div className="ni-dash-app-cover">
        <div className="ni-dash-app-cover-orb" aria-hidden="true" />

        <svg
          className="ni-dash-app-cover-pattern"
          viewBox="0 0 400 100"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id={`niDashPat-${productKey}`}
              x="0"
              y="0"
              width={productKey === "SAPIENS" ? 22 : 20}
              height={productKey === "SAPIENS" ? 22 : 20}
              patternUnits="userSpaceOnUse"
            >
              {productKey === "SAPIENS" ? (
                <path
                  d="M 22 0 L 0 0 0 22"
                  fill="none"
                  stroke="rgba(255,255,255,0.22)"
                  strokeWidth="0.5"
                />
              ) : (
                <circle cx="2" cy="2" r="1" fill="rgba(255,255,255,0.32)" />
              )}
            </pattern>
          </defs>
          <rect
            width="400"
            height="100"
            fill={`url(#niDashPat-${productKey})`}
          />
        </svg>

        <span className={`ni-dash-app-status ${statusClass}`}>
          {statusLabel}
        </span>
      </div>

      {/* Icon tile */}
      <div className="ni-dash-app-icon-tile" aria-hidden="true">
        <img src={productLogo} alt="" />
      </div>

      {/* Body */}
      <div className="ni-dash-app-body">
        <div className="ni-dash-app-eyebrow">Bizro</div>
        <h3 className="ni-dash-app-name">{productTitle}</h3>
        <p className="ni-dash-app-desc">{productDescription}</p>

        {/* Meta row */}
        <div className="ni-dash-app-meta">
          {isBought && !comingSoon && !isExpired && (
            <span className="ni-dash-app-tag ni-dash-app-tag--success">
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Subscribed
            </span>
          )}

          {/* Only show "Add to plan" and "Beta access" if the user can actually subscribe */}
          {!isBought && !comingSoon && canSubscribe && (
            <span className="ni-dash-app-tag ni-dash-app-tag--brand">
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add to plan
            </span>
          )}
          {productKey === "SAPIENS" &&
            isEffective &&
            !isBought &&
            !comingSoon &&
            canSubscribe && (
              <span className="ni-dash-app-tag ni-dash-app-tag--info">
                ✦ Beta access
              </span>
            )}
          {comingSoon && (
            <span className="ni-dash-app-tag ni-dash-app-tag--info">
              ✦ Launching soon
            </span>
          )}
        </div>

        <button
          type="button"
          className={`ni-dash-app-cta ${
            ctaDisabled
              ? "ni-dash-app-cta--soon"
              : ctaIsRenewal
                ? "ni-dash-app-cta--renew"
                : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            if (!ctaDisabled) handleProductClick();
          }}
          disabled={ctaDisabled}
        >
          <span style={{ textTransform: "capitalize" }}>{ctaLabel}</span>
          {!ctaDisabled && (
            <svg
              className="ni-dash-app-cta-arrow"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default Index;